// Rebus session builder + answer checking. Difficulty = mean LLM solve-rate (LOW = HARD),
// which doubles as the human-vs-LLM comparison anchor — so the test spreads across the range,
// one puzzle per band, exactly like the cryptic + 24 tests.

import { REBUS_PUZZLES as ALL_REBUS_PUZZLES, RebusPuzzle } from './rebus.data'
import { REBUS_CONDITIONS } from './rebus.conditions'
import { REBUS_PRACTICE } from './practice.data'
import { DROPPED_IDS } from './rpds'
import { matchSpokenAnswer } from '../../../shared/labs/answerMatch'

// Playable set = canonical 100 minus puzzles dropped from the sample (currently 19_10). 99 total.
export const REBUS_PUZZLES: RebusPuzzle[] = ALL_REBUS_PUZZLES.filter((p) => !DROPPED_IDS.has(p.id))

export type RebusDifficulty = 'easy' | 'medium' | 'hard'

export const tierOf = (llmSolveRate: number): RebusDifficulty =>
  llmSolveRate >= 0.66 ? 'easy' : llmSolveRate >= 0.33 ? 'medium' : 'hard'

export type RebusSessionPuzzle = RebusPuzzle & {
  difficulty: RebusDifficulty
  band: number | null // test-mode difficulty band (0 hardest … n-1 easiest); null in free play
  conditionId: number | null // pre-determined test-group id (24-game model); null for free play
}

const decorate = (p: RebusPuzzle, band: number | null, conditionId: number | null = null): RebusSessionPuzzle => ({
  ...p,
  difficulty: tierOf(p.llmSolveRate),
  band,
  conditionId,
})

const shuffle = <T>(arr: readonly T[]): T[] => {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

// TEST mode: `n` puzzles across the difficulty range (one per contiguous LLM-solve-rate band,
// band 0 = hardest), shuffled for presentation.
export const buildRebusTest = (n = 5): RebusSessionPuzzle[] => {
  const byHardest = [...REBUS_PUZZLES].sort((a, b) => a.llmSolveRate - b.llmSolveRate)
  const size = Math.floor(byHardest.length / n)
  const picked: RebusSessionPuzzle[] = []
  for (let band = 0; band < n; band++) {
    const start = band * size
    const slice = band === n - 1 ? byHardest.slice(start) : byHardest.slice(start, start + size)
    picked.push(decorate(slice[Math.floor(Math.random() * slice.length)], band))
  }
  return shuffle(picked)
}

// TEST mode (24-game model): pre-determined groups of 10 allocated at random. A session gets ONE
// whole condition (one puzzle per band, decile 0 hardest … 9 easiest), each tagged with its band +
// conditionId, then shuffled. Migrated from the per-band random draw (buildRebusTest) — see schema v3.
export const buildRebusTestFromConditions = (): RebusSessionPuzzle[] => {
  const byId = new Map(REBUS_PUZZLES.map((p) => [p.id, p]))
  const conditionId = Math.floor(Math.random() * REBUS_CONDITIONS.length)
  const picked = REBUS_CONDITIONS[conditionId]
    .map((id, band) => {
      const p = byId.get(id)
      return p ? decorate(p, band, conditionId) : null
    })
    .filter((x): x is RebusSessionPuzzle => x !== null)
  return shuffle(picked)
}

// WARM-UP: one random reserved practice puzzle (unscored, never in the test/free-play pool).
// The player sees the warm-up at most once, so a single random pick keeps it low-stakes.
export const pickRebusPractice = (): RebusSessionPuzzle =>
  decorate(REBUS_PRACTICE[Math.floor(Math.random() * REBUS_PRACTICE.length)], null)

// FREE PLAY: one random puzzle, optionally filtered to a coarse tier.
export const pickRebusPlay = (difficulty?: RebusDifficulty): RebusSessionPuzzle => {
  const pool = difficulty ? REBUS_PUZZLES.filter((p) => tierOf(p.llmSolveRate) === difficulty) : REBUS_PUZZLES
  const src = pool.length ? pool : REBUS_PUZZLES
  return decorate(src[Math.floor(Math.random() * src.length)], null)
}

// ---- answer checking ----
// Normalise: uppercase, strip everything but A–Z and 0–9 (so spacing/punctuation don't matter —
// "eye sockets" == "EyeSockets"). Rebus answers are short phrases.
export const normalizeAnswer = (s: string): string => s.toUpperCase().replace(/[^A-Z0-9]/g, '')

export type RebusVerdict = { correct: boolean; matched: string | null }

// Every accepted form, with pipe-alternatives split out ("Snow|Snot" -> "Snow","Snot") and any
// hand-curated spoken/homophone variants folded in.
export const acceptedForms = (puzzle: RebusPuzzle): string[] =>
  [puzzle.answer, ...puzzle.alternatives, ...(puzzle.spokenVariants || [])]
    .flatMap((s) => s.split('|'))
    .map((s) => s.trim())
    .filter(Boolean)

export const checkRebusAnswer = (input: string, puzzle: RebusPuzzle): RebusVerdict => {
  const guess = normalizeAnswer(input)
  if (!guess) return { correct: false, matched: null }
  const forms = acceptedForms(puzzle)
  const exact = forms.find((a) => normalizeAnswer(a) === guess)
  if (exact) return { correct: true, matched: exact }
  // tolerate an answer phrased inside a sentence (typed or spoken), e.g. "it's a mancave"
  const spoken = matchSpokenAnswer(input, forms)
  return { correct: !!spoken, matched: spoken }
}

// Detect the spoken answer in the committed transcript, to pre-fill the answer box after commit.
export const detectRebusAnswer = (transcript: string, puzzle: RebusPuzzle): string | null =>
  matchSpokenAnswer(transcript, acceptedForms(puzzle))
